// JavaScript source code
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes
const WARNING_TIME = 60 * 1000; // Show warning 1 minute before timeout

export default function useSessionTimeout() {
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(INACTIVITY_LIMIT / 1000);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const timerRef = useRef(null);
    const countdownRef = useRef(null);

    // Reset timer on any user activity
    const resetTimer = useCallback(() => {
        // Clear existing timers
        if (timerRef.current) clearTimeout(timerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        // Reset countdown
        setTimeLeft(INACTIVITY_LIMIT / 1000);
        setShowWarning(false);

        // Set new timeout
        timerRef.current = setTimeout(() => {
            setShowWarning(true);

            // Start countdown
            let secondsLeft = WARNING_TIME / 1000;
            setTimeLeft(secondsLeft);

            countdownRef.current = setInterval(() => {
                secondsLeft -= 1;
                setTimeLeft(secondsLeft);

                if (secondsLeft <= 0) {
                    clearInterval(countdownRef.current);
                    handleLogout();
                }
            }, 1000);
        }, INACTIVITY_LIMIT - WARNING_TIME);
    }, []);

    const handleLogout = () => {
        // Clear timers
        if (timerRef.current) clearTimeout(timerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        // Show session expired message
        localStorage.setItem('sessionExpired', 'true');

        // Dispatch logout
        dispatch(logout());
        navigate('/login');
    };

    const handleStayLoggedIn = () => {
        resetTimer();
    };

    // Set up activity listeners
    useEffect(() => {
        const events = [
            'mousedown',
            'mousemove',
            'keydown',
            'scroll',
            'touchstart',
            'click',
            'keypress',
        ];

        // Start initial timer
        resetTimer();

        // Add event listeners
        events.forEach((event) => {
            window.addEventListener(event, resetTimer);
        });

        // Cleanup
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
            events.forEach((event) => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [resetTimer]);

    return {
        showWarning,
        timeLeft,
        handleStayLoggedIn,
        handleLogout,
    };
}